import React from 'react';
import { Briefcase, Mail, Sparkles, Truck, Users } from 'lucide-react';
import { SEO } from '../SEO';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { PhotoStrip } from '../PhotoStrip';
import { HeroPhoto } from '../HeroPhoto';
import { PAGE_STRIPS, PHOTO_LIBRARY } from '../../data/pagePhotos';

const careers = [
  {
    title: 'Professional Movers',
    icon: Truck,
    summary: 'Join our moving crew to handle residential, apartment, office, and same-day support jobs across Columbus and surrounding Indiana service areas.',
    responsibilities: [
      'Safely load, unload, wrap, and protect customer belongings',
      'Show up on time, communicate clearly, and represent the company professionally',
      'Handle stairs, tight spaces, and heavy items with care and teamwork',
    ],
    fit: [
      'Moving, delivery, logistics, warehouse, or labor experience is a plus',
      'Able to lift, carry, and stay active throughout the day',
      'Reliable transportation and strong work ethic required',
    ],
    subject: 'Application - Professional Movers',
  },
  {
    title: 'Professional Cleaners',
    icon: Sparkles,
    summary: 'Join our cleaning team for residential, move-in, move-out, deep-clean, and recurring service appointments.',
    responsibilities: [
      'Deliver detailed, consistent cleaning in homes, apartments, and offices',
      'Use checklists, time blocks, and team coordination to keep jobs on schedule',
      'Maintain a respectful, trustworthy experience in every client space',
    ],
    fit: [
      'Cleaning or housekeeping experience is preferred but not required',
      'Strong eye for detail and pride in leaving spaces spotless',
      'Dependable attendance and professional communication required',
    ],
    subject: 'Application - Professional Cleaners',
  },
];

export const CareersPage = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO
        title="Careers | Movers and Cleaners Jobs in Indiana"
        description="Apply for careers at Swift Move and Clean. We are hiring professional movers and professional cleaners. Send your application to info@swiftmoveandclean.com."
        keywords="Swift Move and Clean careers, mover jobs Indiana, cleaner jobs Indiana, moving company jobs Columbus IN, cleaning jobs Columbus IN"
        canonical="/careers"
      />

      <section
        className="relative isolate overflow-hidden text-white"
        style={{
          background: 'linear-gradient(135deg, #16357a 0%, #1e3a8a 46%, #0f7cb5 100%)',
        }}
      >
        <HeroPhoto src={PHOTO_LIBRARY.dresserCarry.src} />
        <div className="absolute inset-0 opacity-25">
          <div
            className="absolute -left-16 top-12 h-48 w-48 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(255,255,255,0.35)' }}
          />
          <div
            className="absolute right-0 top-0 h-56 w-56 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(251, 191, 36, 0.28)' }}
          />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,19,53,0.05),rgba(7,19,53,0.28))]" />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#f8fafc] to-transparent opacity-20" />
        <div className="container relative mx-auto min-h-[480px] px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-3xl pt-8">
            <div
              className="mb-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold"
              style={{ backgroundColor: 'rgba(255,255,255,0.16)' }}
            >
              <Briefcase className="h-4 w-4 text-white" />
              <span className="text-white">Careers at Swift Move and Clean</span>
            </div>
            <h1
              className="text-4xl font-black tracking-tight sm:text-5xl"
              style={{ color: '#ffffff', textShadow: '0 10px 30px rgba(7,19,53,0.22)' }}
            >
              Build a Career With a Team That Shows Up and Gets the Job Done Right
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8" style={{ color: 'rgba(239, 246, 255, 0.96)' }}>
              We are hiring dependable people who take pride in clean work, careful service, and
              professional customer communication. For every role below, candidates should apply by
              sending their application to <strong className="text-white">info@swiftmoveandclean.com</strong>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                className="bg-white text-navy-blue hover:bg-blue-50"
                onClick={() => window.location.href = 'mailto:info@swiftmoveandclean.com?subject=Application%20for%20Swift%20Move%20and%20Clean'}
              >
                <Mail className="mr-2 h-4 w-4" />
                Apply by Email
              </Button>
              <Button
                variant="outline"
                className="hover:text-navy-blue"
                style={{
                  borderColor: 'rgba(255,255,255,0.9)',
                  color: '#ffffff',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                }}
                onClick={() => document.getElementById('open-roles')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Users className="mr-2 h-4 w-4" />
                View Open Roles
              </Button>
            </div>
          </div>
        </div>
      </section>

      <PhotoStrip heading={PAGE_STRIPS.careers.heading} photos={PAGE_STRIPS.careers.photos} />

      <section id="open-roles" className="bg-gradient-to-br from-gray-50 to-blue-50 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-blue">Open Roles</p>
            <h2 className="mt-3 text-3xl font-black text-gray-900 sm:text-4xl">
              Current Opportunities
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Every applicant should email their application, resume, and any relevant experience to{' '}
              <a className="font-semibold text-sky-blue hover:underline" href="mailto:info@swiftmoveandclean.com">
                info@swiftmoveandclean.com
              </a>.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            {careers.map((career) => {
              const Icon = career.icon;
              return (
                <Card key={career.title} className="border-0 shadow-xl shadow-slate-200/70">
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="mb-4 inline-flex rounded-2xl bg-sky-100 p-3 text-sky-blue">
                          <Icon className="h-6 w-6" />
                        </div>
                        <h3 className="text-2xl font-black text-gray-900">{career.title}</h3>
                      </div>
                      <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-orange-700">
                        Hiring
                      </span>
                    </div>

                    <p className="mt-4 text-base leading-7 text-gray-600">{career.summary}</p>

                    <div className="mt-8">
                      <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-gray-500">What You’ll Do</h4>
                      <ul className="mt-4 space-y-3 text-gray-700">
                        {career.responsibilities.map((item) => (
                          <li key={item} className="flex gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-sky-blue" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-8">
                      <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-gray-500">Good Fit If You Are</h4>
                      <ul className="mt-4 space-y-3 text-gray-700">
                        {career.fit.map((item) => (
                          <li key={item} className="flex gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-bright-orange" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-8 rounded-2xl bg-navy-blue p-5 text-white">
                      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-100">How to Apply</p>
                      <p className="mt-3 text-base leading-7 text-blue-50">
                        Candidates should apply by sending their application to{' '}
                        <strong>info@swiftmoveandclean.com</strong>. Include your resume, phone number,
                        and a short note about your experience.
                      </p>
                      <Button
                        className="mt-5 bg-bright-orange text-white hover:opacity-90"
                        onClick={() => window.location.href = `mailto:info@swiftmoveandclean.com?subject=${encodeURIComponent(career.subject)}`}
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        Apply for {career.title}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
